import React, {
  ComponentProps,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { TwitterPicker } from "react-color";
import { useOutsideAlerter } from "../../utils/outsideAlerter";
import {
  createBannerColors,
  DARK_COLORS,
  DEFAULT_COLOR,
} from "../../utils/publicBannerColors";
import { AuthContext } from "../Authorization/AuthContext";
import { Button } from "../Common/Button";
import {
  applyTwitterPickerOverrides,
  ColorSwatch,
  Container,
  PickerContainer,
  TwitterPickerStyles,
} from "./__styles__/ColorPicker";
import Wrapper from "./Wrapper";

const SWATCH_OVERLAY = "A";

interface ColorPickerProps {
  name: string;
  value: string;
  onChange: (color: string) => void;
  label?: string;
  compactLabel?: boolean;
  helperText?: string;
  error?: string;
  disabled?: boolean;
  required?: boolean;
  labelSize?: ComponentProps<typeof Wrapper>["labelSize"];
}

export const ColorPicker = ({
  label,
  name,
  value,
  onChange,
  disabled = false,
  required = false,
  compactLabel,
  helperText,
  error,
  labelSize,
}: ColorPickerProps) => {
  const [showPicker, setShowPicker] = useState(false);
  const { account } = useContext(AuthContext);
  const headerColor = account?.publicPortal?.color;
  const swatchColors = createBannerColors(headerColor);

  const handleChangeComplete = (color: { hex: string }) => {
    onChange(color.hex);
    setShowPicker(false);
  };

  // Swatch colors should always be uppercase but the value may not be - we capitalize both to ensure a match
  const matchedColor = swatchColors.find(
    color => color.color.value.toUpperCase() === value.toUpperCase()
  );

  // If the value doesn't match any swatch color, update the form with the default color
  useEffect(() => {
    if (!matchedColor && value !== DEFAULT_COLOR.color.value) {
      onChange(DEFAULT_COLOR.color.value);
    }
  }, [value, matchedColor, onChange]);

  const selectedColor = matchedColor || DEFAULT_COLOR;
  const swatchesPerRow = DARK_COLORS.length + (headerColor ? 1 : 0);

  const containerRef = useRef<HTMLDivElement>(null);

  useOutsideAlerter({
    ref: containerRef,
    onOutsideInteraction: () => setShowPicker(false),
  });

  const handleClick = () => {
    setShowPicker(prev => !prev);
  };

  const twitterPickerGlobalStyles = applyTwitterPickerOverrides(
    SWATCH_OVERLAY,
    swatchColors,
    swatchesPerRow
  );
  twitterPickerGlobalStyles();

  return (
    <Wrapper
      label={label}
      name={name}
      error={error}
      required={required}
      compactLabel={compactLabel}
      labelSize={labelSize}
      helperText={helperText}
      printValue={selectedColor.color.value}
    >
      <Container ref={containerRef}>
        <Button
          size="medium"
          styleVariant="outlineLight"
          type="button"
          disabled={disabled}
          onClick={handleClick}
        >
          <ColorSwatch
            style={{
              backgroundColor: selectedColor.color.value,
              color: selectedColor.textColor,
            }}
            withBorder={selectedColor.color.value === "#FFFFFF"}
          >
            {SWATCH_OVERLAY}
          </ColorSwatch>
          {selectedColor.name}
        </Button>

        {showPicker && (
          <PickerContainer>
            <TwitterPicker
              color={selectedColor.color.value}
              onChangeComplete={handleChangeComplete}
              triangle="hide"
              colors={swatchColors.map(color => color.color.value)}
              styles={TwitterPickerStyles}
            />
          </PickerContainer>
        )}
      </Container>
    </Wrapper>
  );
};
